import {
    OrbitControls,
    Vector2
} from 'da-three';

import AnimationManager from './manager/AnimationManager.js';
import EventDispatcher from './service/EventDispatcher.js';
import LightManager from './manager/LightManager.js';
import MaterialManager from './manager/MaterialManager.js';
import HighlightManager from './manager/HighlightManager.js';
import RenderManager from './manager/RenderManager.js';
import SceneManager from './manager/SceneManager.js';
import CWSPLScene from '../plugins/CWSPLScene.js';

export default class DAStudio{
    constructor(container, Config) {

        this.config = Config;
        this.container = container;
        this.mouse = new Vector2();
        this.flyingLightOn = this.config.features.useFloatingLight.default;
        this.highlightsOn = this.config.features.showHighlight.default;

        this.dispatcher = new EventDispatcher();
        this.dispatcher.setEventTypes([
            "afterRender",
            "afterWindowResize",
            "animationRunning",
            "animationHook",
            "animationState",
            "beforeObjectLoad",
            "beforeRender",
            "beforeWindowResize",
            "highlightClicked", 
            "navigateEnd", 
            "navigateStart", 
            "objectComplete", 
            "objectProgress",
            "setFlyingLight", 
            "setHighlights",
            "toggleState"
        ]);

        // SCENE MANAGER
        this.sceneMan = new SceneManager(this.dispatcher,this.config);
        this.camera = this.sceneMan.camera;
        this.scene = this.sceneMan.scene;
        this.sceneMan.dispatcher.addEventListener('beforeObjectLoad',(event,progress) => this.dispatcher.dispatch('beforeLoad',progress));
        this.sceneMan.dispatcher.addEventListener('objectComplete',this.onObjectComplete.bind(this));
        this.sceneMan.dispatcher.addEventListener('objectProgress', (event, progress) => this.dispatcher.dispatch('objectProgress', progress));
        
        // ANIMATION MANAGER
        this.animMan = new AnimationManager(this.dispatcher);
        this.animMan.dispatcher.addEventListener('animationRunning', (event, action) => this.dispatcher.dispatch('animationRunning', action));
        this.animMan.dispatcher.addEventListener('animationHook', (event, data) => this.dispatcher.dispatch('animationHook', data));
        this.animMan.dispatcher.addEventListener('animationState', (event, data) => this.dispatcher.dispatch('animationState', data));

        // RENDER MANAGER
        this.renderMan = new RenderManager(this.dispatcher,this.config,this.sceneMan);
        this.container.appendChild(this.renderMan.renderer.domElement);
        this.renderMan.dispatcher.addEventListener('afterRender',() => this.dispatcher.dispatch('afterRender'));
        this.renderMan.dispatcher.addEventListener('beforeRender',() => this.dispatcher.dispatch('beforeRender'));

        // MATERIAL MANAGER
        this.matMan = new MaterialManager(
            this.dispatcher,
            this.config,
            this.config.materials,
            this.config.environments,
            this.config.project.basedir,
            this.config.project.folder,
            this.renderMan.renderer
        );
        this.matMan.setWireframeEnabled(this.config.features.useWireframe.default);

        // CONTROLS
        this.controls = new OrbitControls( this.camera, this.renderMan.renderer.domElement );
        this.controls.dampingFactor = 0.15;
        this.controls.enableDamping = true;
        this.controls.rotateSpeed = 0.15;
        this.controls.autoRotateSpeed = 0.04;
        this.controls.autoRotate = this.config.features.useAutoRotate.default;
        this.controls.enablePan = true;
        this.controls.panSpeed = 0.1;
        this.controls.maxDistance = 500;
        this.controls.screenSpacePanning = true;

        // HIGHLIGHT MANAGER
        this.highlightMan = new HighlightManager(this.dispatcher,
            this.config,
            this.controls,
            this.camera,
            this.sceneMan,
            this.renderMan,
            this.animMan);
        this.highlightMan.highlightsOn = this.highlightsOn;
        this.highlightMan.dispatcher.addEventListener("highlightClicked", (event,hl) => this.dispatcher.dispatch('highlightClicked',hl));

        // VIDEO & BACKGROUND IMAGE
        this.initVideo();

        // LIGHT MANAGER
        this.lightMan = new LightManager(this.dispatcher,this.config,this.sceneMan);
        this.lightMan.flyingLightOn = this.flyingLightOn;
        this.lightMan.dispatcher.addEventListener('setFlyingLight',(value) => this.dispatcher.dispatch('setFlyingLight',value));

        // MANAGER INITIALIZATION
        this.sceneMan.init(this.renderMan,this.matMan);

        // EVENT LISTENER
        window.addEventListener('resize', this.onWindowResize.bind(this), false);
        

        this.onWindowResize();
        // REMOVE ME!
        this.plugins = {
            cws: new CWSPLScene(this)
        }


        // Listen to events
        window.addEventListener('message', (event) => {
            let data = typeof event.data === 'object' ? event.data : JSON.parse(event.data);

            const { type } = data;

            if(type === 'resizeWindow') {
                this.onWindowResize();
            }
        });
    }
            
    
    
    changeMaterialColor(color,materialName){
        this.matMan.changeMaterialColor(color,materialName);
    }
    
    
    
    getAnimationNames(id){
        return this.animMan.getAnimationNamesByObjectID(id);
    }
    
    
    
    getManagerByName(name){
        switch(name){
            case 'Animations':
                return this.animMan;
            case 'Highlights':
                return this.highlightMan;
            case 'Lighting':
                return this.lightMan;
            case 'Materials':
                return this.matMan;
            case 'Rendering':
                return this.renderMan;
            case 'Scene':
                return this.sceneMan;
            default:
                return null;
        }
    }
    
    
    
    gotStream(stream){
        if (window.URL) {
            this.camvideo.src = window.URL.createObjectURL(stream);   
        } else {// Opera
            this.camvideo.src = stream;   
        }

        this.camvideo.onerror = function(e){   
            stream.stop();
        };

        stream.onended = this.noStream;
    }
    
    
    
    hoverHighlight(id,active){
        this.highlightMan.hoverHighlight(id,active);
    }
    
    
    
    initVideo() {
        if (this.config.features.useVideo.active){
            navigator.getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia;
            window.URL = window.URL || window.webkitURL;

            this.camvideo = document.getElementById('monitor');

            if (!navigator.getUserMedia) {
                if (this.config.tech.debug) console.log('DEBUG ===== Sorry. navigator.getUserMedia() is not available');
            } else {
                navigator.getUserMedia({video: true}, this.gotStream.bind(this), this.noStream.bind(this));
            }
        } else {
            document.getElementById('videoFeatures').remove();
        }
    }
    
    setHighlightAnimations(value) {
        this.highlightMan.segueOnAnimation = value;
    }
    
    noStream(e){
        var msg = 'No camera available.';
        if (e.code === 1){
            msg = 'User denied access to use camera.';
        }
        if (this.config.tech.debug) console.log('DEBUG ===== '+msg);
    }
        
    
    
    onObjectComplete(event,object){
        if (this.flyingLightOn){
            this.flyingLightOn = false;
            this.toggleFlyingLight();
        }
        this.dispatcher.dispatch('objectComplete', object);
        console.log("Object Load complete");
        this.onWindowResize();
        // could be the moment where we can notify plugins
    }
    
    
    
    onWindowResize() {
        console.log("Run window resize event");
        this.dispatcher.dispatch('beforeWindowResize');
        this.dispatcher.dispatch('afterWindowResize');
    }
    
    
    
    selectAnimation(objectID,animName){
        return this.animMan.selectAnimation(objectID,animName);
    }
    
    
    
    setAnimationTime(time, force = false){
        this.animMan.setAnimationTime(time, force);
    }
    
    
    
    setPostprocessingOn(value){
        this.renderMan.setPostprocessingOn(value);
    }
    
    
    
    setWireframeEnabled(newValue){
        this.matMan.setWireframeEnabled(newValue);
    }
    
    
    
    toggleAnimation(play){
        this.animMan.animationToggle(play);
    }
    
    
    
    toggleClickzones(value){
        this.highlightMan.toggleClickzones(value);
    }
    
    
    
    toggleFlyingLight(){
        this.lightMan.toggleFlyingLight();
    }
    
    
    
    togglePostprocess(name){
        this.renderMan.togglePostprocess(name);
    }
    
    
    
    toggleSkyBox(active){
        this.sceneMan.toggleSkyBox(active);
    }
    
    
    
    toggleZoom(active){
        this.controls.enableZoom = active;
    }
    
        
    
    triggerHighlight(id, immediate = false, fromStart = false){
        this.highlightMan.triggerHighlight(id, immediate, fromStart);
    }
};


// WEBPACK FOOTER //
// ./src/js/studio/DAStudio.js